import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { Draggable } from '@hello-pangea/dnd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowRightLong, faArrowLeftLong, faArrowDownLong, faArrowUpLong, faArrowDown19,
    faArrowUp19
} from '@fortawesome/free-solid-svg-icons';
import { LuArrowDownUp } from "react-icons/lu";

import moment from 'moment';
import { TaskContext } from './../context';
import 'react-datepicker/dist/react-datepicker.css';
import ReminderDateTimePicker from './Task/ReminderDateTimePicker';
import axios from 'axios';
import ConfirmationPopup from './ConfirmationPopup';
import TaskData from './Task/TaskData';
import { useNavigate } from 'react-router-dom';
import CONSTANTS from '../CONSTANTS.json';
import services from '../services';
import ChildTask from './ChildTask';
import TaskDropdownModal from './TaskDropdownModal';
import { HiMailOpen } from 'react-icons/hi';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { LiaTagsSolid } from 'react-icons/lia';
import { FaLink } from 'react-icons/fa';
import { GoGitCompare } from 'react-icons/go';
import LabelIcon from "@mui/icons-material/Label";
import { Tooltip } from "react-tooltip";
import { getLocalStorageItem } from '../utils/Localstorage/localStorageUtils';
import {
    setSelectedMyDayPlanDate,
    setSelectedReminderDate,
    setSelectedStatusNextStepDate,
    setNote,
    setNotesText,
    setMyDayDate,
    setDeadlineDate,
    setIsMyDayChecked,
    setIsDeadlineChecked,
} from '../Redux/slice';

const today = new Date();

const allDaysData = [
    {
        deadline: 'Today',
        dayName: CONSTANTS.daysOfWeek[today.getDay()],
    },
    {
        deadline: 'Tomorrow',
        dayName: CONSTANTS.daysOfWeek[(today.getDay() + 1) % 7],
    },
    {
        deadline: 'In two days',
        dayName: CONSTANTS.daysOfWeek[(today.getDay() + 2) % 7],
    },
    {
        deadline: 'Next week',
        dayName: CONSTANTS.daysOfWeek[(today.getDay() + 5) % 7],
    },
    {
        deadline: 'Pick a date',
        dayName: '',
    },
    {
        deadline: 'Add this item to My Day plan',
        dayName: '',
    },
];

export const useTaskData = () => {
    const dispatch = useDispatch();
    const {
        selectedMyDayPlanDate,
        selectedReminderDate,
        selectedStatusNextStepDate,
        note,
        notesText,
        myDayDate,
        deadlineDate,
        isMyDayChecked,
        isDeadlineChecked,
    } = useSelector((state) => state.redux);

    return {
        selectedMyDayPlanDate,
        setSelectedMyDayPlanDate: (v) => dispatch(setSelectedMyDayPlanDate(v)),
        selectedReminderDate,
        setSelectedReminderDate: (v) => dispatch(setSelectedReminderDate(v)),
        selectedStatusNextStepDate,
        setSelectedStatusNextStepDate: (v) => dispatch(setSelectedStatusNextStepDate(v)),
        note,
        setNote: (v) => dispatch(setNote(v)),
        notesText,
        setNotesText: (v) => dispatch(setNotesText(v)),
        myDayDate,
        setMyDayDate: (v) => dispatch(setMyDayDate(v)),
        deadlineDate,
        setDeadlineDate: (v) => dispatch(setDeadlineDate(v)),
        isMyDayChecked,
        setIsMyDayChecked: (v) => dispatch(setIsMyDayChecked(v)),
        isDeadlineChecked,
        setIsDeadlineChecked: (v) => dispatch(setIsDeadlineChecked(v)),
    };
};

function CardBadges({ allCustomFieldList, selectedBoardId, item }) {
    const selectedFieldIds = item?.customProperty?.flatMap((cp) => cp?.selectedFieldId || []) || [];

    const statusBadges = (allCustomFieldList || [])
        .filter((f) =>
            f?.customPropertyId &&
            f?.CustomProperty?.boardId === selectedBoardId &&
            selectedFieldIds.includes(f?.id) &&
            f?.CustomProperty?.isVisible === true &&
            f.customField?.name === 'Status' &&
            (typeof f.customField?.value === 'string' || typeof f.customField?.value === 'number')
        )
        .map((f) => ({
            key: `s-${f.id}`,
            type: 'status',
            label: String(f.customField.value),
            color: f.customField?.color || '#9ca3af',
            index: f?.CustomProperty?.index ?? 0,
        }));

    const tagBadges = (allCustomFieldList || [])
        .filter((f) =>
            f?.customPropertyId &&
            f?.CustomProperty?.boardId === selectedBoardId &&
            selectedFieldIds.includes(f?.id) &&
            f?.CustomProperty?.isVisible === true &&
            f.customField?.name === 'Tag' &&
            Array.isArray(f.customField?.value) &&
            f.customField.value.every((t) => t && typeof t.label === 'string')
        )
        .flatMap((f) =>
            f.customField.value.map((tag, i) => ({
                key: `t-${f.id}-${i}`,
                type: 'tag',
                label: `#${tag.label}`,
                color: tag.color || '#2196f3',
                index: f?.CustomProperty?.index ?? 0,
            }))
        );

    // Match the property order shown on the task detail page.
    const all = [...statusBadges, ...tagBadges].sort((a, b) => a.index - b.index);
    if (all.length === 0) return null;

    const Badge = ({ b }) => b.type === 'status'
        ? <span className="cb-badge cb-badge--status" style={{ background: b.color }}>{b.label}</span>
        : <span className="cb-badge cb-badge--tag" style={{ color: b.color, borderColor: b.color }}>{b.label}</span>;

    return (
        <div
            className="cb-row"
            data-no-popup="true"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
        >
            {all.map((b) => <Badge key={b.key} b={b} />)}
        </div>
    );
}

const BOARD_TASK_TYPE_COLORS = {
    SupportTask: { bg: '#ede9fe', text: '#7c3aed' },
    Task: { bg: '#dbeafe', text: '#2563eb' },
    Gmail: { bg: '#fee2e2', text: '#dc2626' },
    Whatsapp: { bg: '#dcfce7', text: '#16a34a' },
    Onboarding: { bg: '#fef3c7', text: '#b45309' },
    Offboarding: { bg: '#fce7f3', text: '#be185d' },
    // product task "Create a Task in kanban" (boardTaskId = booking id)
    ProductBookingTask: { bg: '#ccfbf1', text: '#0f766e' },
};

const boardTaskTypeColorFallback = (type) => {
    const palette = ['#2563eb', '#7c3aed', '#dc2626', '#16a34a', '#b45309', '#be185d', '#0891b2'];
    let hash = 0;
    for (let i = 0; i < type.length; i++) hash = (hash * 31 + type.charCodeAt(i)) >>> 0;
    return palette[hash % palette.length];
};

function BoardTaskTypeBadge({ boardTaskType }) {
    if (!boardTaskType) return null;
    const label = boardTaskType.replace(/([a-z])([A-Z])/g, '$1 $2');
    const preset = BOARD_TASK_TYPE_COLORS[boardTaskType];
    const text = preset?.text || boardTaskTypeColorFallback(boardTaskType);
    const bg = preset?.bg || `${text}1a`;
    return (
        <div className="board-task-type-row" data-no-popup="true">
            <span
                className="board-task-type-badge"
                style={{ backgroundColor: bg, color: text }}
            >
                {label}
            </span>
        </div>
    );
}

function Task(props) {
    const { updateDeadlineDateInLocalStorage, moduleName } = React.useContext(TaskContext);

    const rawLoggedInUserData = getLocalStorageItem('userData');
    const loggedInUserData = typeof rawLoggedInUserData === 'string' ? JSON.parse(rawLoggedInUserData) : rawLoggedInUserData;
    const canArchiveTicket = loggedInUserData?.role?.toLowerCase() === 'admin' || loggedInUserData?.userArchiveTicket === 'allowed';




    const curretnTaskdata = useSelector((state) => state.redux.currentTask);
    const {
        item,
        moveTaskToColumn,
        indexId,
        removeTask,
        moveTaskDown,
        moveTaskUp,
        getTaskMail,
        columnTitle,
        setTaskDetailsPopup,
        taskDeadline,
        userAssigneeName,
        taskColor,
        taskAssign,
        setSearchTerm,
        searchTerm,
        isShiftPressed,
        moveChildTask,
        columnID,
        handleRemoveChild,
        setIsShiftPressed,
        getTaskMailIndbox,
        handleUpdateTask,
        handleSingleClick,
        handleTaskDetailPopup,
        showModal,
        taskDetailsModal,
        setTaskDetailModal,
        currentTask,
        setCurrentTask,
        editTaskTitle,
        setEditTaskTitle,
        selectedTaskcolor,
        setSelectedTaskcolor,
        isOpen,
        setIsOpen,
        taskDeleteId,
        toggleTaskDropdown,
        showBox,
        setShowBox,
        setIsSearchBoxOpen,
        setHoverIndex,
        setColorIndexID,
        setActivePatch,
        hoverIndex,
        colorIndexID,
        activePatch,
        handleEditTitle,
        clearHighlight,
        handleClick,
        setUpdateTitle,
        updateTitle,
        setIsOpenDeadline,
        isOpenDeadline,
        isSearchBoxOpen,
        deleteTask,
        taskToDelete,
        setTaskToDelete,
        handleCancelDelete,
        clickTimeoutRef,
        ignoreNextClickRef,
        selectedBoardId,
        allTaskData,
        setTaskMail,
        moveTask,
        hoveredIndex,
        setHoveredIndex,
        moveTaskToIndex,
        updateStatusTask,
        createTaskMail,
        setStatusUpdate,
        moveTaskInState,
        moveTaskToOtherColumnInState,
        allCustomFieldList,
        isMember,
        parentOrigin,
        userList
    } = props;




    const [selectedDate, setSelectedDate] = useState(new Date());
    const [isProgress, setIsProgress] = useState(false);
    const [deadline, setDeadline] = useState('');
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [showTime, setShowTime] = useState(false);
    const [taskitemasign, setTaskitemasign] = useState('');
    const [weedDays, setWeekDays] = useState(false);
    const [checked, setChecked] = React.useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [assignedUserName, setAssignedUserName] = useState('');
    const [userName, setUserName] = useState('');
    const [editingTaskId, setEditingTaskId] = useState(null);
    const [openDeadlineTaskId, setOpenDeadlineTaskId] = useState(null);
    let clickTimeout = null;
    const popupRef = useRef(null);
    const [searchBoxTaskId, setSearchBoxTaskId] = useState(null);
    const [taskTitle, setTaskTitle] = useState('');
    const [openUpwards, setOpenUpwards] = useState(false);
    const dropdownRef = useRef();
    const arrowButtonRef = useRef(null);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
    const [calendarPos, setCalendarPos] = useState({ top: 0, left: 0 });
    const userIconRef = useRef(null);
    const [userDropdownPos, setUserDropdownPos] = useState({ top: null, bottom: null, left: 0 });
    const [searchOpenUpwards, setSearchOpenUpwards] = useState(false);
    const searchDropdownRef = useRef();
    const [deadlineOpenUpwards, setDeadlineOpenUpwards] = useState(false);
    const deadlineDropdownRef = useRef();
    const ref = useRef(null);
    const [isHovered, setIsHovered] = useState(false);

    const [isDependencyModalOpen, setIsDependencyModalOpen] = useState(false);
    const [taskForDependency, setTaskForDependency] = useState(null);
    const [dependencySearchTerm, setDependencySearchTerm] = useState('');
    const [initialDependencies, setInitialDependencies] = useState([]);
    const [selectedDependencies, setSelectedDependencies] = useState(new Set());
    const [allDependencies, setAllDependencies] = useState([]);
    const [isDependenciesModalOpen, setIsDependenciesModalOpen] = useState(false);
    const [taskWithDependencies, setTaskWithDependencies] = useState(null);
    const [dependenciesList, setDependenciesList] = useState([]);

    // Scanning every task on the board is only needed while the dependency picker
    // is open — gating it avoids an O(totalTasks) re-scan on every card, on every
    // board render (e.g. each drag-and-drop touches taskMail and re-renders all cards).
    const allTasksForDep = useMemo(() => {
        if (!isDependencyModalOpen) return [];
        return Object.values(allTaskData)
            .flat()
            .filter((task) => task.boardId === selectedBoardId);
    }, [isDependencyModalOpen, allTaskData, selectedBoardId]);

    const fetchAllDependencies = async () => {
        try {
            const response = await services.task.GET_ALL_DEPENDENCIES();
            setAllDependencies(response.data.data);
        } catch (error) {
            console.error('Failed to fetch dependencies', error);
            toast.error('Could not load dependency information.');
        }
    };

    useEffect(() => {

    }, []);

    const openDependencyModal = (item) => {
        const currentDeps = allDependencies
            .filter((dep) => dep.dependerId === item.id)
            .map((dep) => ({
                dependencyId: dep.dependencyId,
                linkId: dep.id,
            }));
        setTaskForDependency(item);
        setInitialDependencies(currentDeps);
        setSelectedDependencies(new Set(currentDeps.map((d) => d.dependencyId)));
        setIsDependencyModalOpen(true);
    };

    const closeDependencyModal = () => {
        setIsDependencyModalOpen(false);
        setTaskForDependency(null);
        setDependencySearchTerm('');
        setInitialDependencies([]);
        setSelectedDependencies(new Set());
    };

    const toggleDependency = (taskId) => {
        setSelectedDependencies((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(taskId)) {
                newSet.delete(taskId);
            } else {
                newSet.add(taskId);
            }
            return newSet;
        });
    };

    const handleSaveDependencies = async () => {
        if (!taskForDependency) return;

        const initialDepIds = new Set(initialDependencies.map((d) => d.dependencyId));
        const finalDepIds = selectedDependencies;

        const toAdd = [...finalDepIds].filter((id) => !initialDepIds.has(id));
        const toRemove = initialDependencies.filter((d) => !finalDepIds.has(d.dependencyId));

        const creationPromises = toAdd.map((dependencyId) =>
            services.task.CREATE_DEPENDENCY({
                dependerId: taskForDependency.id,
                dependencyId: dependencyId,
                type: 'blocks',
            }),
        );

        const deletionPromises = toRemove.map((dep) => services.task.DELETE_DEPENDENCY(dep.linkId));

        try {
            await Promise.all([...creationPromises, ...deletionPromises]);
            toast.success('Dependencies updated successfully!');
            fetchAllDependencies();
            // Silent update for dependencies to avoid scroll reset
            updateLocalTask({ isTaskDependency: finalDepIds.size > 0 });
        } catch (error) {
            console.error('Failed to update dependencies', error);
            toast.error('Failed to update dependencies.');
            // Fallback to refresh on error
            getTaskMail(1, '', selectedBoardId);
        } finally {
            closeDependencyModal();
        }
    };

    const filteredTasksForDep = allTasksForDep.filter(
        (t) =>
            t.id !== (taskForDependency && taskForDependency.id) &&
            (t.title || t.subject || '').toLowerCase().includes(dependencySearchTerm.toLowerCase()),
    );

    useEffect(() => {
        if (isOpen && arrowButtonRef.current) {
            const rect = arrowButtonRef.current.getBoundingClientRect();
            const dropdownHeight = 280;
            const spaceBelow = window.innerHeight - rect.bottom;
            const goUp = spaceBelow < dropdownHeight;
            setOpenUpwards(goUp);
            setDropdownPos({
                top: goUp ? rect.top - dropdownHeight : rect.bottom + 4,
                left: rect.right - 160,
            });
        }
    }, [isOpen]);

    useEffect(() => {
        if (openDeadlineTaskId && deadlineDropdownRef.current) {
            setTimeout(() => {
                const rect = deadlineDropdownRef.current.getBoundingClientRect();
                const spaceBelow = window.innerHeight - rect.bottom;
                if (spaceBelow < 250) {
                    setDeadlineOpenUpwards(true);
                } else {
                    setDeadlineOpenUpwards(false);
                }
            }, 0);
        }
    }, [openDeadlineTaskId]);

    const handleChange = () => {
        setChecked(!checked);
    };

    const navigate = useNavigate();

    const itemId = item?.id;

    const updateTaskMail = async (id, dedlineData) => {
        const msgId = item?.gmailMessageId;
        const data = {
            deadline: dedlineData,
            taskStatus: columnTitle,
            title: editTaskTitle,
            gmailMessageId: msgId,
        };

        try {
            const response = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/task/updateTask/${id}`, data);
            if (response) {
                setUpdateTitle(false);
                // Update locally to avoid scroll reset
                updateLocalTask({
                    deadline: dedlineData,
                    title: editTaskTitle
                });
                setEditingTaskId(null);
                setEditTaskTitle('');
                setTaskTitle('');
            }
        } catch (error) {
            if (error?.response?.status === 401) {
                localStorage.clear();
                navigate('/login');
            }
            // Fallback to refresh
            getTaskMail(1, '', selectedBoardId);
        }
    };

    const handleDoubleClick = (item, e) => {
        if (clickTimeoutRef.current) {
            clearTimeout(clickTimeoutRef.current);
            clickTimeoutRef.current = null;
        }

        ignoreNextClickRef.current = true;
        if (item?.id) {
            setEditingTaskId(item.id);
            setEditTaskTitle(item?.title || item?.subject);
            setTaskTitle(item?.title || item?.subject);
        } else {
            setEditingTaskId(null);
            setEditTaskTitle('');
            setTaskTitle('');
        }
        setCurrentTask(false);
    };

    const selectedColor = CONSTANTS?.color?.find((color) => color.value === taskColor);
    const borderColor = selectedColor ? selectedColor.value1 : '0px';

    const deleteTaskMail = async () => {
        try {
            const response = await services.task.DELETE_TASK(taskDeleteId);
            if (response) {
                getTaskMail();
                setShowBox(false);
            }
        } catch (error) {
            console.error('Error fetching task emails:', error);
        }
    };

    const filteredUsers = taskAssign.filter((user) =>
        user.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const textareaRef = useRef(null);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            updateTaskMail(item?.id);
        }
    };
    useEffect(() => {
        function handleClickOutside(e) {
            if (
                textareaRef.current &&
                !textareaRef.current.contains(e.target)
            ) {
                updateTaskMail(item?.id);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [updateTaskMail, item]);




    const handleDateChange = (date) => {
        if (!date) return;
        updateDeadlineDateInLocalStorage(itemId, date);
        setSelectedDate(date);
        const dedlineData = date.toISOString();
        setDeadline(dedlineData);
        updateTaskMail(item?.id, dedlineData);
        setIsCalendarOpen(false);
        setIsOpenDeadline(false);
        setOpenDeadlineTaskId(null);
    };

    const handleDateOnlyChange = (date) => {
        const next = selectedDate ? new Date(selectedDate) : new Date();
        next.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
        if (!selectedDate) {
            next.setHours(12, 0, 0, 0);
        }
        setSelectedDate(next);
    };

    const deadlineDrop = (id) => {
        setShowTime(!showTime);
        setIsOpenDeadline(!isOpenDeadline);
        setOpenDeadlineTaskId(openDeadlineTaskId === id ? null : id);
    };

    const inTaskProgress = () => {
        setIsProgress(!isProgress);
    };

    const handleDeadlineChange = (e) => {
        setDeadline(e.target.value);
    };

    const handleToggleSearchBox = (itemData) => {
        setTaskitemasign(itemData?.id);
        setSearchBoxTaskId((prev) => (prev === itemData?.id ? null : itemData?.id));
    };

    const handleTodayClick = () => {
        setShowTime(true);
        setWeekDays(true);
        const today = new Date();
        const dedlineData = today.toISOString();
        setDeadline(dedlineData);
        setIsOpenDeadline(false);
        updateTaskMail(item?.id, dedlineData);
    };

    const handleTomorrowClick = () => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setShowTime(true);
        setWeekDays(true);
        setIsOpenDeadline(false);
        const dedlineData = tomorrow.toISOString();
        setDeadline(dedlineData);
        updateTaskMail(item?.id, dedlineData);
    };

    const handleNextTwoDaysClick = () => {
        const nextTwoDays = new Date();
        nextTwoDays.setDate(nextTwoDays.getDate() + 2);
        setShowTime(true);
        setWeekDays(true);
        setIsOpenDeadline(false);
        const dedlineData = nextTwoDays.toISOString();
        setDeadline(dedlineData);
        updateTaskMail(item?.id, dedlineData);
    };

    const handleNextWeekDaysClick = () => {
        const nextWeek = new Date();
        nextWeek.setDate(nextWeek.getDate() + 4);
        setShowTime(true);
        setWeekDays(true);
        setIsOpenDeadline(false);
        const dedlineData = nextWeek.toISOString();
        setDeadline(dedlineData);
        updateTaskMail(item?.id, dedlineData);
    };

    const handleEditTitleNew = (item) => {
        setEditingTaskId(item.id);
        setEditTaskTitle(item?.title || item?.subject);
        setUpdateTitle(true);
        setIsOpen(!isOpen);
        setIsOpenDeadline(false);
    };

    const handleInputBlur = () => {
        setEditingTaskId(null);
        setCurrentTask(false);
    };

    const updateLocalTask = (updatedFields) => {
        if (typeof setTaskMail === 'function') {
            setTaskMail((prev) => {
                const newTasks = { ...prev };
                if (newTasks[columnID]) {
                    newTasks[columnID] = newTasks[columnID].map((t) =>
                        String(t.id) === String(item.id) ? { ...t, ...updatedFields } : t
                    );
                }
                return newTasks;
            });
        }
    };

    const handleTaskSelectedColor = async (selectedColor) => {
        const data = {
            color: selectedColor,
        };
        try {
            const response = await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/task/updateTask/${item?.id}`,
                data,
            );
            // Update locally to avoid full refresh and scroll-to-top
            updateLocalTask({ color: selectedColor });
            setSearchBoxTaskId(null);
        } catch (error) {
            console.error('Error:', error);
            // Fallback to refresh if local update fails or for safety
            getTaskMail(1, '', selectedBoardId);
        }
    };

    const handleUserSelect = async (user) => {
        const data = {
            userId: user,
        };
        try {
            const response = await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/task/updateTask/${item?.id}`,
                data,
            );
            // Update locally to avoid full refresh and scroll-to-top
            updateLocalTask({ userId: user });
            setSearchBoxTaskId(null);
        } catch (error) {
            console.error('err');
            // Fallback to refresh
            getTaskMail(1, '', selectedBoardId);
        }
    };

    const handleEditTitleSave = () => {
        if (editTaskTitle !== (item?.title || item?.subject)) {
            updateTaskMail(editingTaskId, editTaskTitle);
        }
        setEditingTaskId(null);
        setCurrentTask(false);
    };

    useEffect(() => {
        if (taskColor) {
            setActivePatch(taskColor);
        }
    }, [taskColor]);

    useEffect(() => {
        if (userAssigneeName && userList && userList.length > 0) {
            const user = userList.find((u) => u.id === userAssigneeName);
            if (user) {
                setUserName(user.name);
            }
        } else {
            setUserName('');
        }
    }, [userAssigneeName, userList]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (deadlineDropdownRef.current && !deadlineDropdownRef.current.contains(event.target)) {
                setOpenDeadlineTaskId(null);
                setIsOpenDeadline(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.ctrlKey || e.shiftKey) {
                setIsShiftPressed(true);
            }
        };
        const handleKeyUp = (e) => {
            if (!e.ctrlKey && !e.shiftKey) {
                setIsShiftPressed(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, [setIsShiftPressed]);

    useEffect(() => {
        if (item?.deadline) {
            setDeadline(moment(item?.deadline).format('MMM D'));
        }
    }, [item]);

    useEffect(() => {
        if (indexId === 3) {
        }
    }, [item, indexId, taskColor, userAssigneeName]);

    const setIsSearchBoxClose = () => {
        setSearchBoxTaskId(null);
    };

    const handleCreateTask = async (dragItem, item) => {
        try {
            const userData = getLocalStorageItem('userData');

            let finalBoardId = selectedBoardId;
            if (!finalBoardId) {
                const savedBoard = getLocalStorageItem('board');
                if (savedBoard) {
                    finalBoardId = typeof savedBoard === 'object' ? savedBoard.value : savedBoard;
                }
            }

            const { data } = await services.task.CREATE_TASK({
                gmailMessageId: dragItem?.item?.gmailMessageId,
                boardId: finalBoardId,
                boardListId: columnID,
                ...(userData?.role === "User" ? { userId: userData?.id } : {}),
            });
            await moveChildTask({ item: { id: data?.data?.id } }, item);
            getTaskMailIndbox();
        } catch (e) {
            console.error(e);
        }
    };

    const updateStatusTaskMessage = async (gmailMessageId, status) => {
        const data = {
            messageRead: status,
        };
        try {
            const response = await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/email/updatedMessageRead/${gmailMessageId}`,
                data,
            );
            if (response && selectedBoardId) {
                // Silent update for read status
                if (typeof setTaskMail === 'function') {
                    setTaskMail((prev) => {
                        const newTasks = { ...prev };
                        Object.keys(newTasks).forEach(colId => {
                            newTasks[colId] = newTasks[colId].map(t => {
                                // Check main email
                                if (t.Email?.gmailMessageId === gmailMessageId) {
                                    t = { ...t, Email: { ...t.Email, messageRead: status } };
                                }
                                if (t.subEmail?.gmailMessageId === gmailMessageId) {
                                    t = { ...t, subEmail: { ...t.subEmail, messageRead: status } };
                                }
                                // Check subtasks
                                if (t.SubTasks || t.subTasks) {
                                    const subKey = t.SubTasks ? 'SubTasks' : 'subTasks';
                                    t[subKey] = t[subKey].map(sub => {
                                        if (sub.Email?.gmailMessageId === gmailMessageId) {
                                            return { ...sub, Email: { ...sub.Email, messageRead: status } };
                                        }
                                        return sub;
                                    });
                                }
                                return t;
                            });
                        });
                        return newTasks;
                    });
                }
            }
        } catch (error) {
            console.error('Error updating task status:', error);
            getTaskMail(1, '', selectedBoardId);
        }
    };

    const [showArchiveConfirmation, setShowArchiveConfirmation] = useState(false);
    const [taskToArchiveAction, setTaskToArchiveAction] = useState(null);

    const archiveTask = async (item) => {
        try {
            const response = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/task/updateTask/${item?.id}`, {
                isInComplete: true,
            });

            if (response) {
                toast.success('Task archived successfully');
                getTaskMail(1, '', selectedBoardId);
            }
        } catch (error) {
            console.error('Error archiving task:', error);
            if (error.response && error.response.data && error.response.data.message) {
                let taskNames = error.response.data.error.replace(/^Error updating Task:\s*/, "");


                toast.error(taskNames);
            } else {
                toast.error('Failed to archive task');
            }
        }
    };
    const handleArchiveClick = (item) => {
        if (!canArchiveTicket) {
            toast.error('You do not have permission to archive tickets');
            return;
        }

        const itemDependencies = allDependencies.filter((dep) => dep.dependerId === item.id).map((d) => d.dependencyId);

        if (itemDependencies.length > 0) {
            const allTasks = Object.values(allTaskData).flat();
            const incompleteDependencies = itemDependencies.filter((depId) => {
                const dependentTask = allTasks.find((t) => t.id === depId);
                return dependentTask && !dependentTask.isInComplete;

            });

            if (incompleteDependencies.length > 0) {
                const incompleteTaskNames = incompleteDependencies
                    .map((depId) => {
                        const dependentTask = allTasks.find((t) => t.id === depId);
                        return dependentTask ? dependentTask.title || dependentTask.subject : 'an unknown task';
                    })
                    .join(', ');
                toast.error(`Please complete the following dependent tasks first: ${incompleteTaskNames}`);
                return;
            }
        }









        setShowArchiveConfirmation(true);
        setTaskToArchiveAction(item);
    };

    const handleConfirmArchive = () => {
        if (taskToArchiveAction) {
            archiveTask(taskToArchiveAction);
        }
        setShowArchiveConfirmation(false);
        setTaskToArchiveAction(null);
    };

    const handleCancelArchive = () => {
        setShowArchiveConfirmation(false);
        setTaskToArchiveAction(null);
    };
    const sla_alert_action = [
        {
            label: '🔥SLA Due',
            value: 'SLA Due',
            color: 'border border-pink-500 text-pink-600 text-[10px] bg-pink-100',
        },
        {
            label: '⚡SLA Soon',
            value: 'SLA Soon',
            color: 'border border-orange-500 text-orange-600 text-[10px] bg-orange-100',
        },
    ];

    const collectUniqueSlaTags = (item) => {
        const parentTags = item?.addProperty?.slaTags || [];

        const subTaskTags = (item?.SubTasks || []).flatMap((sub) => sub?.addProperty?.slaTags || []);

        const combinedTags = [...parentTags, ...subTaskTags];

        return [...new Set(combinedTags)];
    };



    const hasDependencies = allDependencies.some((dep) => {
        return dep.dependerId === item.id;
    });


    const openDependenciesModal = async (item) => {
        try {
            const response = await services.task.GET_TASK_DEPENDENCIES(item.id);



            const dependentTasks = response.data.data.map((dep) => dep.Dependency);

            setTaskWithDependencies(item);
            setDependenciesList(dependentTasks || []);
            setIsDependenciesModalOpen(true);
        } catch (error) {
            console.error('Failed to fetch dependencies for task', item.id, error);
            toast.error('Could not load dependency information for this task.');
        }
    };

    const closeDependenciesModal = () => {
        setIsDependenciesModalOpen(false);
        setTaskWithDependencies(null);
        setDependenciesList([]);
    };
    const extractEmail = (value, { html = false } = {}) => {
        if (!value) return "";

        const parts = value.split(",").map(v => v.trim()).filter(Boolean);

        const emails = parts.map(part => {
            const match = part.match(/<([^>]+)>/);
            if (match) return match[1];
            if (part.includes("@")) return part;
            return "";
        }).filter(Boolean);

        if (html) {
            return emails.map(e => `<div style="white-space:nowrap;">${e}</div>`).join("");
        }

        return emails.join(", ");
    };




    const isUnreadTask =
        item?.subEmail?.messageRead === 'unread' ||
        item?.Email?.messageRead === 'unread' ||
        (item?.SubTasks || item?.subTasks || []).some(sub =>
            sub?.Email?.messageRead === 'unread' ||
            sub?.subEmail?.messageRead === 'unread'
        );

    const gmailMessageIdToUpdate = item?.Email?.gmailMessageId ||
        item?.subEmail?.gmailMessageId ||
        item?.gmailMessageId ||
        (item?.SubTasks || item?.subTasks || [])?.find(sub => sub?.Email?.messageRead === 'unread' || sub?.subEmail?.messageRead === 'unread')?.gmailMessageId ||
        (item?.SubTasks || item?.subTasks || [])?.find(sub => sub?.Email?.messageRead === 'unread' || sub?.subEmail?.messageRead === 'unread')?.Email?.gmailMessageId;

    return (
        <>
            <Draggable draggableId={String(item.id)} index={indexId}>
                {(provided, snapshot) => (
                    <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={`task-crat ${isUnreadTask ? 'task-crat-blink' : ''}`}
                        style={{
                            ...provided.draggableProps.style,
                            opacity: snapshot.isDragging ? 0.5 : 1,
                            backgroundColor: isUnreadTask ? undefined : item?.color,
                            borderLeft: `5px solid ${borderColor === '0px' ? 'transparent' : borderColor}`,
                            cursor: 'auto',
                        }}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                        onClick={(e) => {
                            if (e.target.closest('[data-no-popup]')) return;
                            if (isUnreadTask) {
                                updateStatusTaskMessage(gmailMessageIdToUpdate, 'read');
                            }
                        }}
                    >
                        <div
                            onClick={(e) => {
                                handleSingleClick(item, e, false);
                            }}
                        >
                            <div className="" onMouseDown={(e) => e.stopPropagation()}>
                                <div className="">
                                    <>
                                        <div className="">
                                            <div className="flex">
                                                {moduleName === 'board' ? (
                                                    <></>
                                                ) : moduleName === 'datePaln' ? (
                                                    <>{item?.deadline ? moment(item.deadline).format('MMM DD') : ''}</>
                                                ) : (
                                                    <></>
                                                )}
                                            </div>

                                            <div>
                                                {/* Row 1: Badges — returns null when empty, no space */}
                                                <CardBadges
                                                    allCustomFieldList={allCustomFieldList}
                                                    selectedBoardId={selectedBoardId}
                                                    item={item}
                                                />

                                                {/* Row 2: From email (left) + Date + chevron (right) */}
                                                <div className="card-meta-row">
                                                    <div className="card-from-col" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                                                        <BoardTaskTypeBadge boardTaskType={item?.boardTaskType} />
                                                        <span
                                                            className="trip-ref-badge"
                                                            // title={item.kaTrip.title}
                                                            style={{
                                                                display: 'inline-block',
                                                                fontSize: '11px',
                                                                fontWeight: 700,
                                                                color: '#b45309',
                                                                background: '#fef3c7',
                                                                border: '1px solid #fde68a',
                                                                borderRadius: '6px',
                                                                padding: '1px 6px',
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                            data-no-popup
                                                        >
                                                            Ref-{item.id.slice(0, 6).toUpperCase()}
                                                        </span>
                                                        {item?.sharedFrom && (
                                                            <span
                                                                data-testid="shared-from-badge"
                                                                title={`Shared from ${item.sharedFrom.boardName || ''} / ${item.sharedFrom.listName || ''} — it stays there too`}
                                                                style={{ display: 'inline-block', fontSize: '10px', fontWeight: 700, color: '#1d4ed8', background: '#dbeafe', border: '1px solid #bfdbfe', borderRadius: '6px', padding: '1px 6px', whiteSpace: 'nowrap', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                                                data-no-popup
                                                            >
                                                                ↗ Shared from {item.sharedFrom.boardName}
                                                            </span>
                                                        )}
                                                        {item?.sharedToCount > 0 && (
                                                            <span
                                                                data-testid="shared-to-badge"
                                                                title={`Also shown on ${item.sharedToCount} other board(s)`}
                                                                style={{ display: 'inline-block', fontSize: '10px', fontWeight: 700, color: '#1d4ed8', background: '#dbeafe', border: '1px solid #bfdbfe', borderRadius: '6px', padding: '1px 6px', whiteSpace: 'nowrap' }}
                                                                data-no-popup
                                                            >
                                                                ↗ Shared ×{item.sharedToCount}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="card-date-col">
                                                        {isUnreadTask && <div className="unread-red-dot"></div>}
                                                        <span
                                                            className={`text-black text-[12px] whitespace-nowrap ${isUnreadTask ? 'font-bold' : 'font-normal'}`}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            {item.isLabel ? <LabelIcon style={{ fontSize: '16px', marginRight: '4px' }} /> : ''}
                                                            {item?.Email?.createdAtEmail ? moment(item.Email.createdAtEmail).format('MMM DD, h:mm') : item?.createdAt ? moment(item.createdAt).format('MMM DD, h:mm') : ''}
                                                        </span>
                                                        <div className="calendar-dropdown calendar-start">
                                                            <div className="absolute-span flex">
                                                                {moduleName === 'board' ? (
                                                                    <>{item?.fetchedAt}</>
                                                                ) : (
                                                                    moduleName === 'datePaln' && (
                                                                        <>
                                                                            <div className="text-gray-700 text-[13px]">
                                                                                {item?.currentTime}
                                                                            </div>
                                                                        </>
                                                                    )
                                                                )}
                                                                <button
                                                                    ref={arrowButtonRef}
                                                                    className={`arrow functionality-open ${isOpen ? 'open' : ''}`}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        toggleTaskDropdown(item?.id, item);
                                                                    }}
                                                                    data-no-popup
                                                                >
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        width="10"
                                                                        height="10"
                                                                        fill="currentColor"
                                                                        className="bi bi-chevron-down down-bottom"
                                                                        viewBox="0 0 16 16"
                                                                    >
                                                                        <path
                                                                            fillRule="evenodd"
                                                                            d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708"
                                                                        />
                                                                    </svg>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                {item?.taskType === "Gmail" && item?.Email && (
                                                    <div className="text-[12px] text-gray-500 flex gap-1 items-center min-w-0">
                                                        <LuArrowDownUp
                                                            data-tooltip-id="my-tooltip"
                                                            data-tooltip-html={`<div style="display:grid;grid-template-columns:70px auto;row-gap:6px;"><div><strong>From</strong></div><div>${extractEmail(item?.Email?.sentBy || item?.sentBy, { html: true })}</div><div><strong>To</strong></div><div>${extractEmail(item?.Email?.sentTo || item?.sentTo, { html: true })}</div></div>`}
                                                            className="text-gray-400 hover:text-blue-500 flex-shrink-0"
                                                        />
                                                        <span className="truncate min-w-0">
                                                            From : {extractEmail(item?.Email?.sentBy || item?.sentBy, { html: false })}
                                                        </span>
                                                    </div>
                                                )}
                                                {/* Full-width content below header */}
                                                {editingTaskId !== item.id ? (
                                                    <>
                                                        <h3
                                                            className="title-box"
                                                            onDoubleClick={(e) => {
                                                                handleDoubleClick(item, e);
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    alignItems: 'start',
                                                                    minWidth: 0,
                                                                    width: '100%',
                                                                    gap: '7px',
                                                                }}
                                                            >
                                                                {item?.nextStep && (
                                                                    <div
                                                                        className="flex items-center gap-1 border-1"
                                                                        style={{ maxWidth: '100%', overflow: 'hidden' }}
                                                                        data-tooltip-id="my-tooltip"
                                                                        data-tooltip-content="Next Step"
                                                                    >
                                                                        <div className="h-[20px] flex items-center gap-x-[10px] flex-shrink-0">
                                                                            <img src="assets/date.svg" alt="Date" />
                                                                        </div>
                                                                        <div style={{ fontSize: '10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                            {item?.nextStep
                                                                                ? moment
                                                                                    .utc(item.nextStep)
                                                                                    .format('dddd, h:mm A')
                                                                                : ''}
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {collectUniqueSlaTags(item).some(tag => sla_alert_action.some(a => a.value === tag)) && (
                                                                    <div className="flex flex-wrap gap-2">
                                                                        {collectUniqueSlaTags(item).map((tag, index) => {
                                                                            const matched = sla_alert_action.find(
                                                                                (action) => action.value === tag,
                                                                            );
                                                                            return matched ? (
                                                                                <div
                                                                                    key={index}
                                                                                    className={`px-2 py-1 rounded-md flex text-[10px] items-center gap-1 ${matched.color}`}
                                                                                >
                                                                                    <span><LiaTagsSolid /></span>
                                                                                    <span>{matched.label}</span>
                                                                                </div>
                                                                            ) : null;
                                                                        })}
                                                                    </div>
                                                                )}

                                                                <div className="flex items-center gap-2 min-w-0 w-full">
                                                                    {item?.taskType === 'Gmail' ? (
                                                                        <HiMailOpen className="w-4 h-4 flex-shrink-0" />
                                                                    ) : item?.taskType === 'Whatsapp' ? (
                                                                        <img
                                                                            src="assets/whatsapp.svg"
                                                                            alt="whatsapp"
                                                                            className="w-4 h-4 flex-shrink-0"
                                                                        />
                                                                    ) : null}
                                                                    <span
                                                                        className={`truncate min-w-0 flex-1 ${isUnreadTask
                                                                            ? 'font-bold'
                                                                            : 'font-normal'
                                                                            }`}
                                                                        style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                                        title={
                                                                            item?.taskType === 'Whatsapp'
                                                                                ? item?.title || item?.name
                                                                                : item?.taskType === 'Gmail'
                                                                                    ? item?.title?.trim()
                                                                                        ? item?.title
                                                                                        : item?.subject
                                                                                    : ''
                                                                        }
                                                                    >
                                                                        {item?.taskType === 'Whatsapp'
                                                                            ? item?.title || item?.name
                                                                            : item?.taskType === 'Gmail'
                                                                                ? item?.title?.trim()
                                                                                    ? item?.title
                                                                                    : item?.subject
                                                                                : null}
                                                                    </span>
                                                                </div>
                                                                {item?.myDayPlan && (
                                                                    <div
                                                                        style={{ maxWidth: '100%', overflow: 'hidden' }}
                                                                        className={`flex items-center gap-1 border-1 ${isUnreadTask
                                                                            ? 'font-bold'
                                                                            : 'font-normal'
                                                                            }`}
                                                                        data-tooltip-id="my-tooltip"
                                                                        data-tooltip-content="My Day Plan"
                                                                    >
                                                                        <div className="h-[20px] flex items-center gap-x-[10px] flex-shrink-0">
                                                                            <svg
                                                                                xmlns="http://www.w3.org/2000/svg"
                                                                                width="16"
                                                                                height="10"
                                                                                fill="currentColor"
                                                                                className="bi bi-flag"
                                                                                viewBox="0 0 10 16"
                                                                            >
                                                                                <path d="M14.778.085A.5.5 0 0 1 15 .5V8a.5.5 0 0 1-.314.464L14.5 8l.186.464-.003.001-.006.003-.023.009a12 12 0 0 1-.397.15c-.264.095-.631.223-1.047.35-.816.252-1.879.523-2.71.523-.847 0-1.548-.28-2.158-.525l-.028-.01C7.68 8.71 7.14 8.5 6.5 8.5c-.7 0-1.638.23-2.437.477A20 20 0 0 0 3 9.342V15.5a.5.5 0 0 1-1 0V.5a.5.5 0 0 1 1 0v.282c.226-.079.496-.17.79-.26C4.606.272 5.67 0 6.5 0c.84 0 1.524.277 2.121.519l.043.018C9.286.788 9.828 1 10.5 1c.7 0 1.638-.23 2.437-.477a20 20 0 0 0 1.349-.476l.019-.007.004-.002h.001M14 1.221c-.22.078-.48.167-.766.255-.81.252-1.872.523-2.734.523-.886 0-1.592-.286-2.203-.534l-.008-.003C7.662 1.21 7.139 1 6.5 1c-.669 0-1.606.229-2.415.478A21 21 0 0 0 3 1.845v6.433c.22-.078.48-.167.766-.255C4.576 7.77 5.638 7.5 6.5 7.5c.847 0 1.548.28 2.158.525l.028.01C9.32 8.29 9.86 8.5 10.5 8.5c.668 0 1.606-.229 2.415-.478A21 21 0 0 0 14 7.655V1.222z" />
                                                                            </svg>
                                                                        </div>
                                                                        <div
                                                                            style={{ fontSize: '10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                                                            className={
                                                                                isUnreadTask
                                                                                    ? 'font-bold'
                                                                                    : 'font-normal'
                                                                            }
                                                                        >
                                                                            {item?.myDayPlan
                                                                                ? moment(item.myDayPlan).format('dddd, h:mm A')
                                                                                : ''}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </h3>
                                                    </>
                                                ) : (
                                                    <>
                                                        <textarea
                                                            ref={textareaRef}
                                                            className="resize-textarea input-static1 outline-text !h-auto overflow-hidden focus:border-none !focus:outline-none placeholder:text-[#000] max-h-40"
                                                            placeholder={editTaskTitle}
                                                            value={editTaskTitle}
                                                            onInput={(e) => {
                                                                e.target.style.height = 'auto';
                                                                e.target.style.height = `${e.target.scrollHeight}px`;
                                                            }}
                                                            onChange={(e) => {
                                                                setEditTaskTitle(e.target.value);
                                                                setTaskTitle(e.target.value);
                                                            }}
                                                            onKeyDown={handleKeyDown}
                                                            onBlur={() => {
                                                                handleEditTitleSave();
                                                            }}
                                                            data-no-popup
                                                            autoFocus
                                                        />
                                                    </>
                                                )}
                                            </div>
                                            <div>
                                                {userName && (
                                                    <div className="assigned-user text-black">{userName}</div>
                                                )}
                                            </div>
                                            {(item?.SubTasks && item?.SubTasks.length > 0) || (item?.subTasks && item?.subTasks.length > 0) ? (
                                                <div
                                                    style={{
                                                        marginTop: '10px',
                                                        paddingLeft: '15px',
                                                        borderLeft: '2px solid #ccc',
                                                    }}
                                                >
                                                    {(item.SubTasks || item.subTasks).map((subTask) => (
                                                        <div key={subTask.id}>
                                                            <ChildTask
                                                                item={subTask}
                                                                moveChildTask={moveChildTask}

                                                                highlightClass={
                                                                    isUnreadTask
                                                                        ? 'font-bold'
                                                                        : 'font-normal'
                                                                }
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : null}


                                            <div className="grid-disp">
                                                <div className="calendar-dropdown master-calendar deadline-align-task">
                                                    {moduleName === 'datePaln' ? (
                                                        <span className="">
                                                            <span
                                                                className={`${showTime ? 'open' : ''} deadline-btn`}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    inTaskProgress();
                                                                }}
                                                            >
                                                                Ideas
                                                            </span>
                                                        </span>
                                                    ) : (
                                                        <span className="calendar-child calendar-datee">
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                width="10"
                                                                height="10"
                                                                fill="currentColor"
                                                                className="bi bi-flag flag-deadline"
                                                                viewBox="0 0 16 16"
                                                            >
                                                                <path d="M14.778.085A.5.5 0 0 1 15 .5V8a.5.5 0 0 1-.314.464L14.5 8l.186.464-.003.001-.006.003-.023.009a12 12 0 0 1-.397.15c-.264.095-.631.223-1.047.35-.816.252-1.879.523-2.71.523-.847 0-1.548-.28-2.158-.525l-.028-.01C7.68 8.71 7.14 8.5 6.5 8.5c-.7 0-1.638.23-2.437.477A20 20 0 0 0 3 9.342V15.5a.5.5 0 0 1-1 0V.5a.5.5 0 0 1 1 0v.282c.226-.079.496-.17.79-.26C4.606.272 5.67 0 6.5 0c.84 0 1.524.277 2.121.519l.043.018C9.286.788 9.828 1 10.5 1c.7 0 1.638-.23 2.437-.477a20 20 0 0 0 1.349-.476l.019-.007.004-.002h.001M14 1.221c-.22.078-.48.167-.766.255-.81.252-1.872.523-2.734.523-.886 0-1.592-.286-2.203-.534l-.008-.003C7.662 1.21 7.139 1 6.5 1c-.669 0-1.606.229-2.415.478A21 21 0 0 0 3 1.845v6.433c.22-.078.48-.167.766-.255C4.576 7.77 5.638 7.5 6.5 7.5c.847 0 1.548.28 2.158.525l.028.01C9.32 8.29 9.86 8.5 10.5 8.5c.668 0 1.606-.229 2.415-.478A21 21 0 0 0 14 7.655V1.222z" />
                                                            </svg>
                                                            <span
                                                                className={`deadline-btn text-black ${showTime ? 'open' : ''} ${isUnreadTask
                                                                    ? 'font-bold'
                                                                    : 'font-normal'
                                                                    }`}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    deadlineDrop(item?.id);
                                                                }}
                                                                data-no-popup
                                                            >
                                                                {item?.deadline
                                                                    ? moment(item?.deadline).format('DD MMMM')
                                                                    : 'Deadline'}
                                                            </span>
                                                        </span>
                                                    )}
                                                </div>
                                                <div
                                                    className={`${userAssigneeName || item?.assignees?.length ? 'inline-flex items-center' : 'svg-circle'
                                                        } absolute-span-1`}
                                                >
                                                    {isHovered && (
                                                        <>

                                                            {item.isTaskDependency && (
                                                                <span
                                                                    className="circle-user cursor-pointer mr-2"
                                                                    title="This task has dependencies"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        openDependenciesModal(item);
                                                                    }}
                                                                >
                                                                    <GoGitCompare className="w-4 h-4 text-indigo-600" />
                                                                </span>
                                                            )}
                                                            <span className="circle-user cursor-pointer mr-2" title="Set Dependencies">
                                                                <FaLink
                                                                    className="w-4 h-4 text-gray-600 hover:text-gray-800"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        openDependencyModal(item);
                                                                    }}
                                                                />
                                                            </span>
                                                            {canArchiveTicket && (
                                                                <span className="circle-user cursor-pointer mr-1">
                                                                    <svg
                                                                        width="22"
                                                                        height="22"
                                                                        viewBox="0 0 24 24"
                                                                        fill="none"
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleArchiveClick(item);
                                                                        }}
                                                                    >
                                                                        <path
                                                                            d="M7.75 12.0001L10.58 14.8301L16.25 9.17012"
                                                                            stroke="#7d7d7d"
                                                                            strokeWidth="2.5"
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                        />
                                                                    </svg>
                                                                </span>
                                                            )}
                                                        </>
                                                    )}
                                                    <div>
                                                        <span
                                                            ref={userIconRef}
                                                            className={`circle-user ${userAssigneeName
                                                                ? 'ml-[5px] user-border text-[#fff]'
                                                                : ''
                                                                } `}
                                                            style={userAssigneeName ? { background: 'linear-gradient(135deg, #1a7d3e 0%, #34a85a 100%)' } : {}}
                                                            title={userAssigneeName ? (userName || userAssigneeName) : 'Unassigned'}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                const rect = e.currentTarget.getBoundingClientRect();
                                                                const dropdownW = 256;
                                                                const estimatedH = 280;
                                                                const spaceBelow = window.innerHeight - rect.bottom;
                                                                const goUp = spaceBelow < estimatedH;
                                                                setSearchOpenUpwards(goUp);
                                                                const left = Math.max(8, Math.min(rect.right - dropdownW, window.innerWidth - dropdownW - 8));
                                                                if (goUp) {
                                                                    // Use bottom so the dropdown's bottom edge sits just above the icon — no height estimation needed
                                                                    setUserDropdownPos({ top: null, bottom: window.innerHeight - rect.top + 4, left });
                                                                } else {
                                                                    setUserDropdownPos({ top: rect.bottom + 6, bottom: null, left });
                                                                }
                                                                handleToggleSearchBox(item);
                                                            }}
                                                            data-no-popup
                                                        >
                                                            {userAssigneeName ? (
                                                                <div className="circle-user-initial ">
                                                                    {userName && userName.charAt(0).toUpperCase()}
                                                                </div>
                                                            ) : (
                                                                <img
                                                                    src="./assets/person.svg"
                                                                    alt="person"
                                                                    className="bi bi-person"
                                                                />
                                                            )}
                                                        </span>
                                                    </div>
                                                    {Array.isArray(item?.assignees) && item.assignees.length > 0 && (
                                                        <span
                                                            className="ml-[3px] rounded-full text-white flex items-center justify-center font-bold"
                                                            style={{ background: '#34a85a', fontSize: 10, minWidth: 20, height: 20, padding: '0 4px' }}
                                                            title={`Also assigned: ${item.assignees.map((a) => a.name || a.email).join(', ')}`}
                                                            data-testid="extra-assignee-badge"
                                                        >
                                                            +{item.assignees.length}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            <div>
                                                {item?.notes && (
                                                    <div className="text-sm text-gray-800 whitespace-pre-wrap break-words max-w-md leading-snug">
                                                        <span
                                                            className="inline-block align-top"
                                                            style={{ fontSize: '12px', minWidth: '70px' }}
                                                        >
                                                            Next Step:
                                                        </span>
                                                        <span
                                                            className="inline-block align-top overflow-hidden"
                                                            style={{
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 1,
                                                                WebkitBoxOrient: 'vertical',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                fontSize: '12px',
                                                                maxWidth: '300px',
                                                                verticalAlign: 'top',
                                                            }}
                                                            title={item.notes}
                                                        >
                                                            {item.notes}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                </div>
                                {item.columnId !== 1 && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            moveTaskToColumn(item.id, item.columnId - 1);
                                        }}
                                        className="button__left btn_desktop button"
                                    >
                                        <FontAwesomeIcon icon={faArrowLeftLong} />
                                    </button>
                                )}
                                {item.columnId !== 4 && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            moveTaskToColumn(item.id, item.columnId + 1);
                                        }}
                                        className="button__right btn_desktop button"
                                    >
                                        <FontAwesomeIcon icon={faArrowRightLong} />
                                    </button>
                                )}
                                {item.columnId !== 1 && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            moveTaskUp(item.id);
                                        }}
                                        className="button__left btn_mobile button"
                                    >
                                        <FontAwesomeIcon icon={faArrowUpLong} />
                                    </button>
                                )}
                                {item.columnId !== 4 && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            moveTaskDown(item.id);
                                        }}
                                        className="button__right btn_mobile button"
                                    >
                                        <FontAwesomeIcon icon={faArrowDownLong} />
                                    </button>
                                )}
                            </div>
                        </div>

                        <div
                            ref={deadlineDropdownRef}
                            onClick={() => deadlineDrop(item.id)}
                            className={`${openDeadlineTaskId === item.id ? 'block' : 'hidden'}  ${deadlineOpenUpwards ? 'dropdown-up-deadline' : ''
                                }`}
                        >
                            <div
                                className={`dropdown ${openDeadlineTaskId === item.id ? 'open' : ''}
                     ${deadlineOpenUpwards ? 'dropdown-up-deadline' : ''}
                    deadline-day deadend-start deadline-dropdown kanban-dropDown`}
                            >
                                <div className="ulList">
                                    <ul className="ulname-list items-list">
                                        <div className="deadline-text">
                                            <h3 className="deadline-head">Deadline...</h3>
                                            <span className="clear-button clr-position">Clear</span>
                                        </div>
                                        {allDaysData?.map((item) => {
                                            return (
                                                <>
                                                    <li key={item?.id}>
                                                        <div
                                                            id={item}
                                                            className="text-listdrop daysof-week"
                                                            data-no-popup
                                                            onClick={() => {
                                                                switch (item?.deadline) {
                                                                    case 'Today':
                                                                        handleTodayClick();
                                                                        break;
                                                                    case 'Tomorrow':
                                                                        handleTomorrowClick();
                                                                        break;
                                                                    case 'In two days':
                                                                        handleNextTwoDaysClick();
                                                                        break;
                                                                    case 'Next week':
                                                                        handleNextWeekDaysClick();
                                                                        break;
                                                                    case 'Pick a date':
                                                                        if (isCalendarOpen !== true) {
                                                                            if (deadlineDropdownRef.current) {
                                                                                const rect = deadlineDropdownRef.current.getBoundingClientRect();
                                                                                const calWidth = 310;
                                                                                const left = rect.right + 4 + calWidth > window.innerWidth
                                                                                    ? Math.max(8, rect.left - calWidth - 4)
                                                                                    : rect.right + 4;
                                                                                setCalendarPos({ top: rect.top, left });
                                                                            }
                                                                            setIsCalendarOpen(true);
                                                                        } else {
                                                                            setIsCalendarOpen(false);
                                                                        }
                                                                        break;
                                                                    default:
                                                                        break;
                                                                }
                                                            }}
                                                        >
                                                            {item?.deadline === 'Today' && (
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    width="16"
                                                                    height="16"
                                                                    fill="currentColor"
                                                                    className="bi bi-brightness-alt-high-fill inlineBlock"
                                                                    viewBox="0 0 16 16"
                                                                >
                                                                    <path d="M8 3a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-1 0v-2A.5.5 0 0 1 8 3m8 8a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1 0-1h2a.5.5 0 0 1 .5.5m-13.5.5a.5.5 0 0 0 0-1h-2a.5.5 0 0 0 0 1zm11.157-6.157a.5.5 0 0 1 0 .707l-1.414 1.414a.5.5 0 1 1-.707-.707l1.414-1.414a.5.5 0 0 1 .707 0m-9.9 2.121a.5.5 0 0 0 .707-.707L3.05 5.343a.5.5 0 1 0-.707.707zM8 7a4 4 0 0 0-4 4 .5.5 0 0 0 .5.5h7a.5.5 0 0 0 .5-.5 4 4 0 0 0-4-4" />
                                                                </svg>
                                                            )}
                                                            {item?.deadline === 'Tomorrow' && (
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    width="16"
                                                                    height="16"
                                                                    fill="currentColor"
                                                                    className="bi bi-brightness-high-fill inlineBlock"
                                                                    viewBox="0 0 16 16"
                                                                >
                                                                    <path
                                                                        d="M12 8a4 4 0 1 1-8 0 4 4 0 0 1 8 0M8

 0a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-1 0v-2A.5.5 0 0 1 8 0m0 13a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-1 0v-2A.5.5 0 0 1 8 13m8-5a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1 0-1h2a.5.5 0 0 1 .5.5M3 8a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1 0-1h2A.5.5 0 0 1 3 8m10.657-5.657a.5.5 0 0 1 0 .707l-1.414 1.415a.5.5 0 1 1-.707-.708l1.414-1.414a.5.5 0 0 1 .707 0m-9.193 9.193a.5.5 0 0 1 0 .707L3.05 13.657a.5.5 0 0 1-.707-.707l1.414-1.414a.5.5 0 0 1 .707 0m9.193 2.121a.5.5 0 0 1-.707 0l-1.414-1.414a.5.5 0 0 1 .707-.707l1.414 1.414a.5.5 0 0 1 0 .707M4.464 4.465a.5.5 0 0 1-.707 0L2.343 3.05a.5.5 0 1 1 .707-.707l1.414 1.414a.5.5 0 0 1 0 .708"
                                                                    />
                                                                </svg>
                                                            )}
                                                            {item?.deadline === 'In two days' && (
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    width="16"
                                                                    height="16"
                                                                    fill="currentColor"
                                                                    className="bi bi-clock-history inlineBlock"
                                                                    viewBox="0 0 16 16"
                                                                >
                                                                    <path d="M8.515 1.019A7 7 0 0 0 8 1V0a8 8 0 0 1 .589.022zm2.004.45a7 7 0 0 0-.985-.299l.219-.976q.576.129 1.126.342zm1.37.71a7 7 0 0 0-.439-.27l.493-.87a8 8 0 0 1 .979.654l-.615.789a7 7 0 0 0-.418-.302zm1.834 1.79a7 7 0 0 0-.653-.796l.724-.69q.406.429.747.91zm.744 1.352a7 7 0 0 0-.214-.468l.893-.45a8 8 0 0 1 .45 1.088l-.95.313a7 7 0 0 0-.179-.483m.53 2.507a7 7 0 0 0-.1-1.025l.985-.17q.1.58.116 1.17zm-.131 1.538q.05-.254.081-.51l.993.123a8 8 0 0 1-.23 1.155l-.964-.267q.069-.247.12-.501m-.952 2.379q.276-.436.486-.908l.914.405q-.24.54-.555 1.038zm-.964 1.205q.183-.183.35-.378l.758.653a8 8 0 0 1-.401.432z" />
                                                                    <path d="M8 1a7 7 0 1 0 4.95 11.95l.707.707A8.001 8.001 0 1 1 8 0z" />
                                                                    <path d="M7.5 3a.5.5 0 0 1 .5.5v5.21l3.248 1.856a.5.5 0 0 1-.496.868l-3.5-2A.5.5 0 0 1 7 9V3.5a.5.5 0 0 1 .5-.5" />
                                                                </svg>
                                                            )}
                                                            {item?.deadline === 'Next week' && (
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    width="16"
                                                                    height="16"
                                                                    fill="currentColor"
                                                                    className="bi bi-calendar4-event inlineBlock"
                                                                    viewBox="0 0 16 16"
                                                                >
                                                                    <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5M2 2a1 1 0 0 0-1 1v1h14V3a1 1 0 0 0-1-1zm13 3H1v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1z" />
                                                                    <path d="M11 7.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5z" />
                                                                </svg>
                                                            )}
                                                            {item?.deadline === 'Pick a date' && (
                                                                <div
                                                                    className="highlight-container"
                                                                    style={{ display: 'inline-block' }}
                                                                >
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        width="16"
                                                                        height="16"
                                                                        fill="currentColor"
                                                                        className="bi bi-calendar2-minus inlineBlock"
                                                                        viewBox="0 0 16 16"
                                                                    >
                                                                        <path d="M5.5 10.5A.5.5 0 0 1 6 10h4a.5.5 0 0 1 0 1H6a.5.5 0 0 1-.5-.5" />
                                                                        <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5M2 2a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1z" />
                                                                        <path d="M2.5 4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5H3a.5.5 0 0 1-.5-.5z" />
                                                                    </svg>
                                                                </div>
                                                            )}
                                                            {item?.deadline === 'Add this item to My Day plan' && (
                                                                <input
                                                                    type="checkbox"
                                                                    checked={checked}
                                                                    onChange={handleChange}
                                                                    className="inlineBlock input-Taskk"
                                                                />
                                                            )}
                                                            <div className="currentDay">
                                                                {item?.deadline &&
                                                                    item?.deadline.charAt(0).toUpperCase() +
                                                                    item?.deadline.slice(1)}
                                                                <div>{item.dayName}</div>
                                                            </div>
                                                        </div>
                                                    </li>
                                                </>
                                            );
                                        })}
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className={`ideas ${isProgress ? 'open' : ''} mt-[-268px] ideas-project`}>
                            <div className="max-w-xs p-4 bg-white border rounded shadow-lg">
                                <div className="flex mb-4 justify-between border-b-4">
                                    <input type="text" className="w-full ideastask" />
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        fill="currentColor"
                                        className="bi bi-search text-[#696D72]"
                                        viewBox="0 0 16 16"
                                    >
                                        <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0" />
                                    </svg>
                                </div>
                                <div className="space-y-2">
                                    <div className="border-b pb-2 text-black">
                                        <h2 className="font-semibold border-b-2 text-start">Personal Board</h2>
                                        <ul className="space-y-1 text-start mt-[12px] ml-[10px]">
                                            <li>To do</li>
                                            <li>Working on</li>
                                            <li>Follow up</li>
                                            <li>To Read</li>
                                            <li>Done</li>
                                        </ul>
                                    </div>
                                    <div className="border-b pb-2 text-black">
                                        <h2 className="font-semibold text-start">Project Board</h2>
                                        <ul className="space-y-1 text-start mt-[12px] ml-[10px]">
                                            <li>Ideas</li>
                                            <li>Planned</li>
                                            <li>In progress</li>
                                            <li>Done</li>
                                        </ul>
                                    </div>
                                    <button className="text-[#42B4F4]">Remove from board</button>
                                </div>
                            </div>
                        </div>

                        {showArchiveConfirmation && (
                            <ConfirmationPopup
                                customMessage="Are you sure you want to complete this task?"
                                onCancel={handleCancelArchive}
                                deleteTaskMail={handleConfirmArchive}
                            />
                        )}

                        {showBox === true && (
                            <ConfirmationPopup
                                boardName={'Column'}
                                taskToDeleteId={taskToDelete}
                                onCancel={handleCancelDelete}
                                deleteTaskMail={deleteTask}
                            />
                        )}
                        {taskDetailsModal && (
                            <div className="task-data-popup" ref={popupRef}>
                                <TaskData
                                    isMember={isMember}
                                    parentOrigin={parentOrigin}
                                    taskItem={curretnTaskdata}
                                    gmailMessageID={curretnTaskdata?.gmailMessageId}
                                    updateTaskMail={updateTaskMail}
                                    item={item}
                                    taskDetailsModal={taskDetailsModal}
                                    setTaskDetailModal={setTaskDetailModal}
                                    getTaskMail={getTaskMail}
                                    setCurrentTask={setCurrentTask}
                                    selectedBoardId={selectedBoardId}
                                />
                            </div>
                        )}
                        {isOpen && ReactDOM.createPortal(
                            <>
                                {/* transparent backdrop to close on outside click */}
                                <div
                                    style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
                                    onClick={() => toggleTaskDropdown(item?.id, item)}
                                />
                                <div
                                    ref={dropdownRef}
                                    style={{
                                        position: 'fixed',
                                        top: dropdownPos.top,
                                        left: Math.max(8, dropdownPos.left),
                                        zIndex: 9999,
                                        width: '160px',
                                        backgroundColor: '#ffffff',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '10px',
                                        boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                                        overflow: 'hidden',
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {/* Menu items */}
                                    {[
                                        {
                                            label: 'View Details',
                                            icon: (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="#7d7d7d" viewBox="0 0 16 16">
                                                    <path fillRule="evenodd" d="M8.636 3.5a.5.5 0 0 0-.5-.5H1.5A1.5 1.5 0 0 0 0 4.5v10A1.5 1.5 0 0 0 1.5 16h10a1.5 1.5 0 0 0 1.5-1.5V7.864a.5.5 0 0 0-1 0V14.5a.5.5 0 0 1-.5.5h-10a.5.5 0 0 1-.5-.5v-10a.5.5 0 0 1 .5-.5h6.636a.5.5 0 0 0 .5-.5" />
                                                    <path fillRule="evenodd" d="M16 .5a.5.5 0 0 0-.5-.5h-5a.5.5 0 0 0 0 1h3.793L6.146 9.146a.5.5 0 0 0 .708.708L15 1.707V5.5a.5.5 0 0 0 1 0z" />
                                                </svg>
                                            ),
                                        },
                                        {
                                            label: 'Edit title',
                                            icon: (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="#7d7d7d" viewBox="0 0 16 16">
                                                    <path d="M12.854.146a.5.5 0 0 0-.707 0L10.5 1.793 14.207 5.5l1.647-1.646a.5.5 0 0 0 0-.708zm.646 6.061L9.793 2.5 3.293 9H3.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.207zm-7.468 7.468A.5.5 0 0 1 6 13.5V13h-.5a.5.5 0 0 1-.5-.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.5-.5V10h-.5a.5.5 0 0 1-.175-.032l-.179.178a.5.5 0 0 0-.11.168l-2 5a.5.5 0 0 0 .65.65l5-2a.5.5 0 0 0 .168-.11z" />
                                                </svg>
                                            ),
                                        },
                                    ].map(({ label, icon }) => (
                                        <div
                                            key={label}
                                            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', cursor: 'pointer', fontSize: '12px', color: '#2f2f2f', borderBottom: '1px solid #f0f0f0' }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (label === 'Edit title') {
                                                    handleEditTitleNew(item);
                                                } else if (label === 'View Details') {
                                                    handleTaskDetailPopup(item, e, false);
                                                }
                                                toggleTaskDropdown(item?.id, item);
                                            }}
                                            data-no-popup
                                        >
                                            {icon}
                                            <span>{label}</span>
                                        </div>
                                    ))}
                                    {/* Highlight row */}
                                    <div
                                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', fontSize: '12px', color: '#2f2f2f', borderBottom: '1px solid #f0f0f0' }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="#7d7d7d" viewBox="0 0 16 16">
                                                <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168z" />
                                            </svg>
                                            <span>Highlight</span>
                                        </div>
                                        <span
                                            onClick={(e) => { e.stopPropagation(); clearHighlight(); }}
                                            style={{ fontSize: '11px', color: '#6b7280', cursor: 'pointer', fontWeight: '500' }}
                                            data-no-popup
                                        >
                                            Clear
                                        </span>
                                    </div>
                                    {/* Color circles */}
                                    <div style={{ padding: '8px 10px' }}>
                                        <div className="Grid-Circle">
                                            {CONSTANTS?.color?.map((color, index) => (
                                                <div
                                                    key={index}
                                                    className={`patch ${color.color} ${activePatch === color.color ? 'active' : ''}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleClick(color.value);
                                                        setHoverIndex(color.value1);
                                                        setColorIndexID(indexId);
                                                        handleTaskSelectedColor(color.value);
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </>,
                            document.body
                        )}

                        {String(searchBoxTaskId) === String(item?.id) && ReactDOM.createPortal(
                            <>
                                {/* transparent backdrop — click outside to close */}
                                <div
                                    style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
                                    onClick={() => {
                                        setSearchBoxTaskId(null);
                                        setSearchTerm('');
                                    }}
                                />
                                <div
                                    ref={searchDropdownRef}
                                    style={{
                                        position: 'fixed',
                                        ...(userDropdownPos.top !== null ? { top: userDropdownPos.top } : {}),
                                        ...(userDropdownPos.bottom !== null ? { bottom: userDropdownPos.bottom } : {}),
                                        left: userDropdownPos.left,
                                        zIndex: 9999,
                                        width: '256px',
                                        backgroundColor: '#ffffff',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '12px',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                        padding: '12px',
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        placeholder="Search by name"
                                        style={{
                                            width: '100%',
                                            padding: '6px 10px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '8px',
                                            marginBottom: '8px',
                                            fontSize: '13px',
                                            outline: 'none',
                                            boxSizing: 'border-box',
                                        }}
                                        autoFocus
                                    />
                                    <ul style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: '192px', overflowY: 'auto' }}>
                                        {(searchTerm ? filteredUsers : taskAssign).length === 0 ? (
                                            <li style={{ color: '#9ca3af', fontSize: '13px', padding: '4px 0' }}>No users found</li>
                                        ) : (
                                            (searchTerm ? filteredUsers : taskAssign).map((user) => (
                                                <li
                                                    key={user.id}
                                                    onClick={() => handleUserSelect(user.id)}
                                                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 4px', borderRadius: '6px', cursor: 'pointer' }}
                                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                                >
                                                    <div
                                                        style={{
                                                            width: '32px', height: '32px', borderRadius: '50%',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            fontSize: '13px', fontWeight: '600', color: '#fff',
                                                            background: 'linear-gradient(135deg, #1a7d3e 0%, #34a85a 100%)',
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {user.name?.charAt(0)?.toUpperCase()}
                                                    </div>
                                                    <div style={{ flex: 1, minWidth: 0, fontSize: '13px', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={user.name}>
                                                        {user.name}
                                                    </div>
                                                </li>
                                            ))
                                        )}
                                    </ul>
                                </div>
                            </>,
                            document.body
                        )}

                        {isDependencyModalOpen && taskForDependency && ReactDOM.createPortal(
                            <div
                                style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                                onClick={closeDependencyModal}
                            >
                                <div
                                    style={{ backgroundColor: '#fff', borderRadius: '16px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', width: '100%', maxWidth: '600px', margin: '0 16px' }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '24px 24px 16px', borderBottom: '1px solid #e5e7eb' }}>
                                        <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', margin: 0 }}>
                                            Set Dependencies
                                        </h2>
                                        <button
                                            onClick={closeDependencyModal}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '4px' }}
                                        >
                                            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                    <div style={{ padding: '16px 24px' }}>
                                        <p style={{ marginBottom: '16px', color: '#4b5563', fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            Task: "{taskForDependency.title || taskForDependency.subject}"
                                        </p>
                                        <div style={{ position: 'relative', marginBottom: '16px' }}>
                                            <svg style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: '#9ca3af' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>
                                            <input
                                                type="text"
                                                placeholder="Search tasks..."
                                                style={{ width: '100%', paddingLeft: '40px', paddingRight: '16px', paddingTop: '10px', paddingBottom: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                                                value={dependencySearchTerm}
                                                onChange={(e) => setDependencySearchTerm(e.target.value)}
                                            />
                                        </div>
                                        <div style={{ maxHeight: '280px', overflowY: 'auto', backgroundColor: '#f9fafb', padding: '8px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                {filteredTasksForDep.length > 0 ? (
                                                    filteredTasksForDep.map((t) => (
                                                        <li
                                                            key={t.id}
                                                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', cursor: 'pointer' }}
                                                            onClick={() => toggleDependency(t.id)}
                                                        >
                                                            <span style={{ color: '#1f2937', fontWeight: '500', fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '85%' }} title={t.title || t.subject}>
                                                                {t.title || t.subject}
                                                            </span>
                                                            <div style={{ width: '22px', height: '22px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', border: `2px solid ${selectedDependencies.has(t.id) ? '#1a7d3e' : '#d1d5db'}`, backgroundColor: selectedDependencies.has(t.id) ? '#1a7d3e' : '#fff' }}>
                                                                {selectedDependencies.has(t.id) && (
                                                                    <svg width="14" height="14" fill="none" stroke="white" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                                                    </svg>
                                                                )}
                                                            </div>
                                                        </li>
                                                    ))
                                                ) : (
                                                    <li style={{ textAlign: 'center', color: '#6b7280', padding: '24px 0', fontSize: '14px' }}>
                                                        No tasks found.
                                                    </li>
                                                )}
                                            </ul>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '16px 24px', borderTop: '1px solid #e5e7eb' }}>
                                        <button
                                            onClick={closeDependencyModal}
                                            style={{ padding: '10px 20px', fontWeight: '600', color: '#374151', backgroundColor: '#f3f4f6', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleSaveDependencies}
                                            style={{ padding: '10px 20px', fontWeight: '600', color: '#fff', backgroundColor: '#1a7d3e', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1a6b2e'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1a7d3e'}
                                        >
                                            Save Dependencies
                                        </button>
                                    </div>
                                </div>
                            </div>,
                            document.body
                        )}

                        {isDependenciesModalOpen && taskWithDependencies && (
                            <div
                                className="fixed inset-0 z-[1001] flex items-center justify-center bg-black bg-opacity-50"
                                onClick={closeDependenciesModal}
                            >
                                <div
                                    className="transform-all w-full max-w-lg transform rounded-2xl bg-white p-6 shadow-xl transition-"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className="mb-4 flex items-center justify-between border-b pb-4">
                                        <h2 className="text-2xl font-bold text-gray-800">Task Dependencies</h2>
                                        <button
                                            onClick={closeDependenciesModal}
                                            className="text-gray-500 transition-colors hover:text-gray-800"
                                        >
                                            <svg
                                                className="h-6 w-6"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="2"
                                                    d="M6 18L18 6M6 6l12 12"
                                                ></path>
                                            </svg>
                                        </button>
                                    </div>



                                    <div className="max-h-72 overflow-y-auto rounded-lg border bg-gray-50 p-4">
                                        <ul className="space-y-2">
                                            {dependenciesList.length > 0 ? (
                                                dependenciesList.map((t) => (
                                                    <li
                                                        key={t.id}
                                                        className="flex items-center justify-between rounded-lg bg-white p-4 shadow-sm"
                                                    >
                                                        <span
                                                            className="font-medium text-gray-800 truncate max-w-[80%]"
                                                            title={t.title || t.subject}
                                                        >
                                                            {t.title || t.subject}
                                                        </span>
                                                        <button
                                                            className="font-semibold"
                                                            style={{ color: '#1a7d3e' }}
                                                            onMouseEnter={(e) => e.currentTarget.style.color = '#1a6b2e'}
                                                            onMouseLeave={(e) => e.currentTarget.style.color = '#1a7d3e'}
                                                            onClick={(e) => {
                                                                handleTaskDetailPopup(t, e, false);
                                                                closeDependenciesModal();
                                                            }}
                                                        >
                                                            View
                                                        </button>
                                                    </li>
                                                ))
                                            ) : (
                                                <li className="py-6 text-center text-gray-500">
                                                    No dependencies found for this task.
                                                </li>
                                            )}
                                        </ul>
                                    </div>

                                    <div className="mt-8 flex justify-end border-t pt-6">
                                        <button
                                            onClick={closeDependenciesModal}
                                            className="rounded-lg bg-gray-100 px-6 py-3 font-semibold text-gray-700 transition-colors hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                                        >
                                            Close
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

            </Draggable>
            <Tooltip id="my-tooltip" placement="top" />

            {isCalendarOpen && ReactDOM.createPortal(
                <>
                    <div
                        style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
                        onClick={() => setIsCalendarOpen(false)}
                    />
                    <div
                        className="reminder-dropdown kanban-deadline-calendar-portal"
                        style={{
                            position: 'fixed',
                            top: calendarPos.top,
                            left: calendarPos.left,
                            zIndex: 9999,
                            width: 310,
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <div className="reminder-dropdown__card reminder-dropdown__card--expanded">
                            <ReminderDateTimePicker
                                customDate={selectedDate}
                                setCustomDate={setSelectedDate}
                                onDateOnlyChange={handleDateOnlyChange}
                                onSave={() => handleDateChange(selectedDate)}
                                onToday={() => setSelectedDate(new Date())}
                                onClear={() => setSelectedDate(null)}
                            />
                        </div>
                    </div>
                </>,
                document.body
            )}

        </>

    );
}

Task.propTypes = {
    item: PropTypes.object.isRequired,
    moveTaskToColumn: PropTypes.func.isRequired,
    moveTaskUp: PropTypes.func.isRequired,
    moveTaskDown: PropTypes.func.isRequired,
    removeTask: PropTypes.func.isRequired,
    indexId: PropTypes.number,
    moduleName: PropTypes.string.isRequired,
    getTaskMail: PropTypes.func.isRequired,
    createTaskMail: PropTypes.func.isRequired,
    columnTitle: PropTypes.func.isRequired,
    setTaskDetailsPopup: PropTypes.func.isRequired,
    taskDetailsPopup: PropTypes.bool.isRequired,
    taskDeadline: PropTypes.object.isRequired,
    userAssigneeName: PropTypes.object.isRequired,
    taskColor: PropTypes.object.isRequired,
    handleUpdateTask: PropTypes.func,
};

export default Task;
